import { PRINTOS_SELECTOR } from '@/mount-helper';
import { ui } from '@/api';
import get from 'lodash.get';
import {READY_GATE} from '@/services/bootstrap';
import {useSessionStore} from '@/stores/Session.store';
import services from '@/services/index';

export default function appServiceFactory(services) {
  let currentAppId;
  let sessionStore;

  let thisService = {
    init,
    getCurrentApp,
    getCurrentAppId,
    getAppById,
    hasApp,
    getCurrentAppAliases,
    goToApp,
    getApps,
    getFullApps,
    getExternalApps,
    getECommerceAppAsync,
    getPageId,
    setPageId,
    sanitize,
  };

  return thisService;

  ////////////////////

  function init() {
    sessionStore = useSessionStore();
    currentAppId = findCurrentAppId();
    services.bootstrap.readyGateCleared(READY_GATE.app);
    return true;
  }

  function getCurrentAppId() {
    return currentAppId;
  }

  function findCurrentAppId() {
    // Allow for configuring from global API
    if (ui.app.id) {
      return ui.app.id;
    }

    let appId = getAttributeValue(PRINTOS_SELECTOR, 'app-id');
    if (appId) {
      return appId;
    }

    appId = getAttributeValue('[ng-app]', 'ng-app');
    if (appId) {
      return appId;
    }

    appId = getAttributeValue('[data-ng-app]', 'data-ng-app');
    if (appId) {
      return appId;
    }

    return undefined;
  }

  /**
   * Provides for retrieving the app id and aliases.
   * (This mirrors the work done in `getCurrentAppId` without
   * allowing for short-circuiting once the app id is known.)
   * @return {Array} an array of app aliases
   */
  function getCurrentAppAliases() {
    let aliases = [];
    if (ui.app.id) {
      aliases.push(ui.app.id);
    }

    let appId = getAttributeValue(PRINTOS_SELECTOR, 'app-id');
    if (appId) {
      aliases.push(appId);
    }

    appId = getAttributeValue('[ng-app]', 'ng-app');
    if (appId) {
      aliases.push(appId);
    }

    appId = getAttributeValue('[data-ng-app]', 'data-ng-app');
    if (appId) {
      aliases.push(appId);
    }

    return aliases;
  }

  function getAttributeValue(selectors, attributeName) {
    const element = services.document.querySelector(selectors);
    if (element) {
      return element.getAttribute(attributeName);
    }
    return undefined;
  }

  function getApps(filter = true) {
    let apps = get(sessionStore.cc, 'apps.entity', []);
    return filter ? apps.filter(app => {
      if (app.group && ( !app.groupOptions || app.groupOptions.length === 0 )) {
        return false;
      }

      return true;
    }) : apps;
  }

  function getCurrentApp() {
    return getAppById(currentAppId);
  }

  function getAppById(id) {
    return get(sessionStore.cc, 'apps.entity', []).find(app => {
      return app.appId === id;
    });
  }

  function hasApp(id) {
    return !!get(sessionStore.cc, 'apps.entity', []).find(app => {
      return app.appId === id;
    });
  }

  function goToApp(id, newTab = false) {
    const app = getAppById(id);
    const link = app._links?.self;
    if (link) {
      if (newTab) {
        window.open(link, id);
      }
      else {
        window.location.href = link;
      }
    }
  }

  function getFullApps() {
    return get(sessionStore.cc, 'apps.entity', []).filter(app => {
      return app.type === 'full';
    });
  }

  function getExternalApps() {
    return get(sessionStore.cc, 'apps.entity', []).filter(app => {
      return app.type === 'external';
    });
  }

  function getPageId() {
    return ui.page.id;
  }

  function setPageId(id) {
    ui.page.id = id;
    services.window.dispatchEvent(new CustomEvent('pros-page-changed', {detail: {id}}));
  }

  function getECommerceAppAsync(appId) {
    const config = {
      params: {
        latest: true,
        name: appId,
        type: 'Application',
      }
    };
    let path = 'organizations/entitlements';
    return services.http.get('enforcer', path, config)
      .then(response => {
        let apps = get(response, 'data.applications', []);
        if (apps) {
          return apps.find(app => {
            return app.name === appId;
          });
        }
      });
  }

  function sanitize(value) {
    const sanitizer = document.createElement('div');
    sanitizer.innerHTML = value;
    return sanitizer.textContent;
  }
}
